'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '@/store/hooks'
import { toast } from 'react-toastify'
import { MdArrowBack, MdSave, MdBiotech } from 'react-icons/md'
import {
    getPacsServer,
    createPacsServer,
    updatePacsServer,
    CreatePacsServerData,
} from '@/lib/api/health-integrations/pacs-api'

export default function PacsFormPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { user } = useAuth()
    const isEditing = !!id

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)

    const [formData, setFormData] = useState<CreatePacsServerData>({
        name: '',
        aeTitle: '',
        host: '',
        port: 104,
        active: true,
        wadoRsUrl: '',
        qidoRsUrl: '',
        stowRsUrl: '',
        viewerUrl: '',
        authType: '',
        authCredentials: '',
        settings: {},
    })

    useEffect(() => {
        if (isEditing && id) {
            loadServer(parseInt(id))
        }
    }, [id])

    const loadServer = async (serverId: number) => {
        setLoading(true)
        try {
            const response = await getPacsServer(serverId)
            if (response.success) {
                const server = response.result
                setFormData({
                    name: server.name,
                    aeTitle: server.aeTitle,
                    host: server.host,
                    port: server.port,
                    active: server.active,
                    wadoRsUrl: server.wadoRsUrl || '',
                    qidoRsUrl: server.qidoRsUrl || '',
                    stowRsUrl: server.stowRsUrl || '',
                    viewerUrl: server.viewerUrl || '',
                    authType: server.authType || '',
                    authCredentials: '',
                    settings: server.settings || {},
                })
            } else {
                toast.error(response.error || 'Erro ao carregar servidor')
                navigate('/health-integrations/pacs')
            }
        } catch (error) {
            toast.error('Erro ao carregar servidor')
            navigate('/health-integrations/pacs')
        } finally {
            setLoading(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.name.trim()) {
            toast.error('Informe o nome do servidor')
            return
        }

        if (!formData.aeTitle.trim()) {
            toast.error('Informe o AE Title')
            return
        }

        if (!formData.host.trim()) {
            toast.error('Informe o host')
            return
        }

        setSaving(true)
        try {
            const data = { ...formData }
            if (!data.authCredentials) {
                delete (data as any).authCredentials
            }

            const response = isEditing
                ? await updatePacsServer(parseInt(id!), data)
                : await createPacsServer(data)

            if (response.success) {
                toast.success(
                    `Servidor ${isEditing ? 'atualizado' : 'criado'} com sucesso`
                )
                navigate('/health-integrations/pacs')
            } else {
                toast.error(
                    response.error ||
                    `Erro ao ${isEditing ? 'atualizar' : 'criar'} servidor`
                )
            }
        } catch (error) {
            toast.error(
                `Erro ao ${isEditing ? 'atualizar' : 'criar'} servidor`
            )
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando servidor...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/health-integrations/pacs')}
                    className="btn btn-ghost btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg">
                        <MdBiotech className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            {isEditing ? 'Editar' : 'Novo'} Servidor PACS
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize os dados do servidor'
                                : 'Configure um novo servidor PACS'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold mb-4">
                        Informações Básicas
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control w-full md:col-span-2">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Nome <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full focus:input-primary"
                                placeholder="Ex: PACS Principal"
                                value={formData.name}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        name: e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    AE Title <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full focus:input-primary"
                                placeholder="Ex: CLINICA_AE"
                                value={formData.aeTitle}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        aeTitle: e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label cursor-pointer justify-start gap-2">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-secondary"
                                    checked={formData.active}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            active: e.target.checked,
                                        })
                                    }
                                />
                                <span className="label-text font-medium">
                                    Servidor ativo
                                </span>
                            </label>
                        </div>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold mb-4">
                        Conexão DICOM
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Host <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full focus:input-primary"
                                placeholder="Ex: 192.168.1.100 ou pacs.hospital.com"
                                value={formData.host}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        host: e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Porta <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full focus:input-primary"
                                placeholder="104"
                                value={formData.port}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        port: parseInt(e.target.value) || 104,
                                    })
                                }
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold mb-4">
                        DICOMweb (Opcional)
                    </h2>
                    <div className="grid grid-cols-1 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    WADO-RS URL
                                </span>
                            </label>
                            <input
                                type="url"
                                className="input input-bordered w-full focus:input-primary"
                                placeholder="https://pacs.hospital.com/dicomweb"
                                value={formData.wadoRsUrl}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        wadoRsUrl: e.target.value,
                                    })
                                }
                            />
                            <label className="label">
                                <span className="label-text-alt">
                                    URL para recuperação de imagens (Web Access
                                    to DICOM Objects)
                                </span>
                            </label>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    QIDO-RS URL
                                </span>
                            </label>
                            <input
                                type="url"
                                className="input input-bordered w-full focus:input-primary"
                                placeholder="https://pacs.hospital.com/dicomweb"
                                value={formData.qidoRsUrl}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        qidoRsUrl: e.target.value,
                                    })
                                }
                            />
                            <label className="label">
                                <span className="label-text-alt">
                                    URL para consulta de estudos (Query based on
                                    ID for DICOM Objects)
                                </span>
                            </label>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    STOW-RS URL
                                </span>
                            </label>
                            <input
                                type="url"
                                className="input input-bordered w-full focus:input-primary"
                                placeholder="https://pacs.hospital.com/dicomweb"
                                value={formData.stowRsUrl}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        stowRsUrl: e.target.value,
                                    })
                                }
                            />
                            <label className="label">
                                <span className="label-text-alt">
                                    URL para armazenamento de imagens (Store
                                    Over the Web)
                                </span>
                            </label>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    URL do Visualizador
                                </span>
                            </label>
                            <input
                                type="url"
                                className="input input-bordered w-full focus:input-primary"
                                placeholder="https://viewer.hospital.com/estudos"
                                value={formData.viewerUrl}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        viewerUrl: e.target.value,
                                    })
                                }
                            />
                            <label className="label">
                                <span className="label-text-alt">
                                    URL base para abrir estudos no visualizador
                                    web
                                </span>
                            </label>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/health-integrations/pacs')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSave className="text-xl" />
                        )}
                        {isEditing ? 'Salvar' : 'Criar'}
                    </button>
                </div>
            </form>
        </div>
    )
}
